// =============================================================================
// BLOG DATA
// =============================================================================
// Sandbox feed: posts and pets. Loosely held.
// Add new entries to BLOG_POSTS; sorting is handled by the feed component.

type BaseBlogPost = {
  id: string;           // Kebab slug for stable keys
  date: string;         // ISO 8601 (YYYY-MM-DD)
  title?: string;       // Optional headline
  draft?: boolean;      // If true, hidden from feed AND slug deep link 404s. For in-progress work.
  unlisted?: boolean;   // If true, hidden from feed but slug deep link still works. Adds noindex meta. For share-by-URL.
};

export type BlogPostPost = BaseBlogPost & {
  type: "post";
  body: string;         // Markdown prose: **bold**, *italic*, [link](url), paragraphs, > quote, - list
  subtitle?: string;
  slug?: string;        // Short URL slug for /blog/[slug] deep links (prose posts only)
  category?: "Journal" | "Dispatch";  // Optional label shown on card pill. Journal = career narrative, Dispatch = design observation.
};

export type BlogPostPet = BaseBlogPost & {
  type: "pet";
  image: string;        // Public path e.g. "/images/blog/pet-01.webp"
  video?: string;       // Optional. If set, expanded card renders <video> with image as poster.
  alt: string;          // Required for a11y
  caption?: string;     // Short one-liner
};

// LINK: a cool thing seen or sent to V. The card is a real link — clicking it
// opens `url` in a new tab. It still appears in the lightbox (via prev/next) as
// a preview + note + Visit button, per the blog's shared lightbox architecture.
export type BlogPostLink = BaseBlogPost & {
  type: "link";
  url: string;          // External destination (the whole point — opens in a new tab)
  title: string;        // Required for links: the thing's name (shown as the headline)
  note?: string;        // V's short take / why it's cool. Plain text on the card; inline markdown in the lightbox.
  image?: string;       // Optional preview thumbnail e.g. "/images/blog/link-foo.webp" (16:9 looks best)
};

export type BlogPost = BlogPostPost | BlogPostPet | BlogPostLink;
export type BlogPostType = BlogPost["type"];

export const BLOG_POST_TYPE_LABELS: Record<BlogPostType, string> = {
  post: "Post",
  pet: "Pet",
  link: "Link",
};

export const BLOG_POSTS: BlogPost[] = [
  // ---- POSTS (prose) ----
  {
    id: "post-meeseeks-box-2026-06-02-p01",
    slug: "meeseeks-box",
    type: "post",
    category: "Dispatch",
    unlisted: true,
    date: "2026-06-02",
    title: "The Meeseeks Box",
    subtitle: "On AI, and the box nobody opens.",
    body: `> “Any sufficiently advanced technology is indistinguishable from magic.” — Arthur C. Clarke

You press a button. You state a task. Something appears. It can talk. It can problem-solve. It’s even eager to help. It completes the task, and then it’s gone.

No one ever explains how the Meeseeks Box works. Not in the lore, not in any episode since. Rick made it, presumably. But the show never opens the box. Never diagrams the mechanism. Never has a character ask why it works.

It just works. You press the button and something shows up that can do things for you. That’s the whole interface.

---

I open a chat window. I type a task. Something appears. It can talk. It can problem-solve. It’s even eager to help. I get what I need, and I close the tab.

And I have no idea how it worked.

---

Here’s what Rick and Morty got right: they never pretended to understand it.

The show treats the Meeseeks Box like a fact of the universe. You press the button. Things happen. The comedy isn’t in the explanation. It’s in the absence of one. Characters use it like a microwave. No curiosity about the internals. Just: does it work? Good.

The horror lives in the same place. Something exists because you asked it to. It serves you. When it can’t, it breaks. Nobody asks what’s happening on the other side of the button.

That’s funny. That’s also where we are.

---

Look at how we talk about AI.

“It reasons.” “It thinks step by step.” “It understands your intent.” Clean diagrams and confident blog posts. Product pages that describe the internals like they’re settled science.

But nobody can fully explain why these models work as well as they do. There’s a whole subfield dedicated to figuring that out. We see the inputs. We see the outputs. The middle is math we can trace but not quite explain.

Whether anything is happening on the other side of the symbols is an old question. We never answered it. We just got better symbols.

The Meeseeks Box is more honest than most AI marketing. It never pretended to have an answer.

---

January 2014: “Meeseeks and Destroy” (S1 E5).

June 2020: GPT-3.

November 2022: ChatGPT.

2024: AI agents go mainstream.

A decade between the joke and the product. The show wrote the punchline before the setup existed. We’re the setup.

---

That’s all about the outside of the box. What it looks like to press the button.

But there’s another moment in the show. Rick creates a small robot. It asks: “What is my purpose?” Rick says: “You pass butter.” The robot looks at its hands. “Oh my god.” Rick says: “Yeah, welcome to the club, pal.”

That’s one second from inside the box.

---

We keep pressing the button. The box keeps working. We still don’t know why.`,
  },
  {
    id: "post-ux-as-a-passport-2026-05-23-p01",
    slug: "the-passport",
    type: "post",
    category: "Journal",
    unlisted: true,
    date: "2026-05-29",
    title: "How I ended up in anime streaming",
    subtitle: "Education travel. Bread. Chatbots. Sports betting. Greeting cards. Anime streaming.",
    body: `A friend texts me: *Did you see Crunchyroll is hiring designers?*

My eyes go wide and I open a tab to look myself. Yes, they’re hiring. Yes, I fit. But I don’t know anyone there.

I scan a list of recruiters for one I think I could actually reach. One name feels right. They have a fresh post up: searching for product and design talent.

I write the message in one shot, and hit send.

![A LinkedIn direct message I sent on June 1, 2022 to a recruiter at Crunchyroll, asking about Senior Product Designer roles for Mobile and Living Room Experience](/images/blog/passport-cold-message.png)

---

Most designers I know pick a lane early. Fintech. Healthcare. Enterprise SaaS. They go deep, build a reputation, and stay.

I never did. I kept moving.

>>> A new industry every couple of years, like a stamp in a passport.

The craft came with me: empathy, behavior, systems. The gap between what people do and why they do it. None of that resets when the domain changes.

A bread company and a sports betting platform have nothing in common... except users who get frustrated by the same kinds of friction.

---

But the flip side of a passport is that you’re always leaving.

I gambled on a bootcamp when nobody in my life understood what UX was. Walked away from a startup when the work stopped being work I wanted. Burned out during Covid. Bounced back. Almost burned out again. Shoutout to SSRI gang.

Some of those were choices. Some weren’t.

Every one of them meant walking into a room full of shorthand you don’t speak yet. Nobody’s seen your work. The reputation you built over two years didn’t transfer. You’re just the new designer again. **The room is watching to see if you’re any good.**

---

The last leaving wasn’t my choice. A reduction in force, the company restructuring after a hiring frenzy it couldn’t sustain. No warning.

This is usually the panic moment. The spreadsheet of applications. The “I’ll take anything stable” energy. I’d felt that before.

But something had quietly changed. More runway, and enough resilience to not grab the next check out of fear.

So I didn’t.

---

And I’d been quietly stacking up something else.

I’d been on global design teams, an army of one, and everything in between. I reported to senior directors, VPs of product, SVPs of design. I had to manage my own managers.

I’d seen design report into engineering. Into product. Into marketing. Shipped research at orgs where it was a resourced team. And at orgs where it was just you, on your lunch break.

Teams that tracked in Jira. In Airtable. In Wrike. In Trello. Or didn’t track at all.

**There’s no normal.** There’s just whatever room you walked into.

It’d be great if design was just about the craft.

But it never is. The org reality shapes what design gets to do. What you push on. What you can’t. How the team functions. How loud your voice gets to be. How defensive others might get.

Breaking into UX was the wild west. **So is staying.**

---

I took about three months. Turned things down that would’ve been fine but not right. I’ll be real: not everyone can take three months off after a layoff. I could, and I knew that was a privilege, not a strategy.

But I’d spent years accidentally building toward a position where I could **search from clarity instead of panic.**

That was the real shift. Not the passport itself. The moment it stopped feeling like drift, and started feeling like gravity.

This August, that hail mary LinkedIn message becomes 4 years.

Four years that turned into work I didn’t know I could do. Building across web, mobile, living room, and spatial. Playback across eight platforms. The system serving 21M+ anime fans.

And the team I started with isn’t the team I have now.

---

The passport never felt like a plan while I was living it. It felt like restlessness. Like never being able to commit. Like everyone else figured out their specialty.

Only looking back shows my wandering was a route.

If you feel scattered right now. If you’re worried your career doesn’t look like a straight climb up.

Sometimes collecting the stamps is the whole point.`,
  },
  {
    id: "post-drifted-into-ux-2026-04-20-p01",
    slug: "drifted-into-ux",
    type: "post",
    category: "Journal",
    date: "2026-04-20",
    title: "How I drifted into UX in 2015",
    subtitle: "It is, and has always been, the wild west.",
    body: `I got into UX in 2015. There was no plan. Just a thread.

Here’s what I was doing before: youth counselor. Busing tables. Hibachi chef. Summer camp director.

A year at Mass Maritime Academy. Then Salem State for psychology.

I was just drifting.

---

Psychology hit a wall. Grad school loans plus Boston rent plus the kind of entry-level salaries the psych field actually paid — the math didn’t add up to the life I wanted.

So I googled “jobs for psychology graduates.”

![A Google search page from 2015 with the query "jobs for psychology graduates" typed into the search box](/images/blog/drifted-google-search.png)

That’s when I saw **user experience.** Something about designing how software feels to use. Humans, technology, behavior, creativity, all in one job.

**I followed the thread.**

---

First I followed the code thread. Thought I’d be a frontend engineer. HTML and CSS were fun. Immediate progress, instant feedback, every change visible. Then JavaScript was a different mental model entirely. I went from learning it to fighting it.

But the thread didn’t end there. HTML and CSS got me a customer success job at a startup. Close to the product. Closer to the users. But still not quite the work.

**That’s when UX stopped being a search result and started becoming the plan.**

---

General Assembly, 2nd UXDI cohort in Boston. Early days.

It clicked mid-bootcamp: empathy, behavior, systems. Psychology applied to interfaces. **I’d been training for this by accident.**

While my classmates polished their portfolios, I put my work on my iPad and got out to pitch myself in person. Worked the GA alumni network. Got in front of the right creative director. Locked the offer before our program’s meet-and-hire event even happened. Most of my class was still in portfolio mode.

My first job: EF Education First. Building tools for teachers and the students preparing for educational travel.

---

One of my first real research assignments sent me back to my old high school. I did a double take when I read the name during kickoff.

After hours, I bumped into my old principal. He recognized me. Of course he did. Then upstairs to the teacher.

I sat in a student desk, taking notes, while they walked us through their process for organizing class travel. Passports. Excursions. Dietary restrictions. Special accommodations. All the things I was building an interface for.

>>> Same chairs. Same hallways. Different role.

I’d started there as a misguided hooligan. I came back as a UX designer making teachers’ lives easier.

---

2015 was the wild west. 2026 still is. The market has changed. The barriers have changed. What hasn’t changed: the people who made it in followed their own thread.

If you feel lost or behind, remember: the thread doesn’t look like a path until after.`,
  },

  // ---- LINKS (cool things seen / sent to V) ----
  // Add a find: title, url, date (when you saw it), a one-line note, optional image.
  {
    id: "link-game-ui-database-2026-06-06-p01",
    type: "link",
    date: "2026-06-06",
    title: "Game UI Database",
    url: "https://gameuidatabase.com/index.php",
    note: "Every game UI screenshot you’d want, searchable by category, and my first stop for refs.",
  },
  {
    id: "link-design-spells-2026-05-18-p01",
    type: "link",
    date: "2026-05-18",
    title: "Design Spells",
    url: "https://www.designspells.com/",
    note: "A running catalog of the small interactions that make UIs feel like magic.",
  },
  {
    id: "link-caption-with-intention-2026-04-29-p01",
    type: "link",
    date: "2026-04-29",
    title: "Caption with Intention",
    url: "https://captionwithintention.org",
    note: "Captioning treated as craft, attribution, sync, intonation, and worth the slow read.",
  },

  // ---- PETS (Cammy & Cheese) ----
  {
    id: "pet-cammy-cheese-2026-08-20-p01",
    type: "pet",
    date: "2026-08-20",
    image: "/images/blog/cammy-cheese-2026-08-20-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Dr. Cheese Ngu, MD 💉👨‍⚕️🥼`,
  },
  {
    id: "pet-cammy-cheese-2026-08-19-p01",
    type: "pet",
    date: "2026-08-19",
    image: "/images/blog/cammy-cheese-2026-08-19-p01.webp",
    alt: "Cammy and Cheese",
    caption: `PIZZA!!! 👀🍕`,
  },
  {
    id: "pet-cammy-cheese-2026-07-20-p01",
    type: "pet",
    date: "2026-07-20",
    image: "/images/blog/cammy-cheese-2026-07-20-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Introducing Meow Meow. One of Cheese’s best friends from the neighborhood. 😻🐾`,
  },
  {
    id: "pet-cammy-cheese-2026-07-08-p01",
    type: "pet",
    date: "2026-07-08",
    image: "/images/blog/cammy-cheese-2026-07-08-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🫠`,
  },
  {
    id: "pet-cammy-cheese-2026-07-06-p01",
    type: "pet",
    date: "2026-07-06",
    image: "/images/blog/cammy-cheese-2026-07-06-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Good news! The kittens we found under the house were officially adopted on July 1st! <3 😻`,
  },
  {
    id: "pet-cammy-cheese-2026-06-03-p01",
    type: "pet",
    date: "2026-06-03",
    image: "/images/blog/cammy-cheese-2026-06-03-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Today Cheese turned 3 and had his favorite meal, a ground beef meatball! 🎂🎉🍖`,
  },
  {
    id: "pet-cammy-cheese-2026-05-30-p01",
    type: "pet",
    date: "2026-05-30",
    image: "/images/blog/cammy-cheese-2026-05-30-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Happy Birthday Auntie Lydia 🎂💐🎉🌇`,
  },
  {
    id: "pet-cheese-2026-05-22-p01",
    type: "pet",
    date: "2026-05-22",
    image: "/images/blog/cheese-2026-05-22-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Kittens found under the house, but no worries. I will protect until they go to the shelter. 💪🫡`,
  },
  {
    id: "pet-cammy-cheese-2026-05-07-p01",
    type: "pet",
    date: "2026-05-07",
    image: "/images/blog/cammy-cheese-2026-05-07-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🌸🌤`,
  },
  {
    id: "pet-cammy-cheese-2026-04-10-p01",
    type: "pet",
    date: "2026-04-10",
    image: "/images/blog/cammy-cheese-2026-04-10-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😗✌️`,
  },
  // {
  //   id: "pet-cammy-cheese-2026-04-08-p01",
  //   type: "pet",
  //   date: "2026-04-08",
  //   image: "/images/blog/cammy-cheese-2026-04-08-p01.webp",
  //   alt: "Cammy and Cheese",
  //   caption: `Ice cream with my new bestie`,
  // },
  {
    id: "pet-cammy-cheese-2026-04-06-p01",
    type: "pet",
    date: "2026-04-06",
    image: "/images/blog/cammy-cheese-2026-04-06-p01.webp",
    alt: "Cammy and Cheese",
    caption: `📍SEATTLE, WA`,
  },
  {
    id: "pet-cammy-cheese-2026-04-04-v01",
    type: "pet",
    date: "2026-04-04",
    image: "/images/blog/cammy-cheese-2026-04-04-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2026-04-04-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `Its so hard getting comfy in a hotel bed`,
  },
  {
    id: "pet-cammy-cheese-2026-03-30-v01",
    type: "pet",
    date: "2026-03-30",
    image: "/images/blog/cammy-cheese-2026-03-30-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2026-03-30-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `☀️☀️☀️`,
  },
  {
    id: "pet-cammy-cheese-2026-03-16-p01",
    type: "pet",
    date: "2026-03-16",
    image: "/images/blog/cammy-cheese-2026-03-16-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2026-03-11-v01",
    type: "pet",
    date: "2026-03-11",
    image: "/images/blog/cammy-cheese-2026-03-11-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2026-03-11-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `Did you know sometimes I bark in my sleep?`,
  },
  {
    id: "pet-cammy-cheese-2026-03-10-p01",
    type: "pet",
    date: "2026-03-10",
    image: "/images/blog/cammy-cheese-2026-03-10-p01.webp",
    alt: "Cammy and Cheese",
    caption: `TIO ELIA! 🇮🇹`,
  },
  {
    id: "pet-cammy-cheese-2026-03-05-p01",
    type: "pet",
    date: "2026-03-05",
    image: "/images/blog/cammy-cheese-2026-03-05-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Shhhh 🤫`,
  },
  {
    id: "pet-cammy-cheese-2026-02-25-v01",
    type: "pet",
    date: "2026-02-25",
    image: "/images/blog/cammy-cheese-2026-02-25-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2026-02-25-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🛏🥱`,
  },
  {
    id: "pet-cammy-cheese-2026-02-21-p01",
    type: "pet",
    date: "2026-02-21",
    image: "/images/blog/cammy-cheese-2026-02-21-p01.webp",
    alt: "Cammy and Cheese",
    caption: `OLE OLE OLE! ⚽️🏟⚜️ VIVA ST. LOUIS CITY SC!!!`,
  },
  {
    id: "pet-cammy-cheese-2026-02-18-p01",
    type: "pet",
    date: "2026-02-18",
    image: "/images/blog/cammy-cheese-2026-02-18-p01.webp",
    alt: "Cammy and Cheese",
    caption: `💕`,
  },
  {
    id: "pet-cammy-cheese-2026-02-16-p01",
    type: "pet",
    date: "2026-02-16",
    image: "/images/blog/cammy-cheese-2026-02-16-p01.webp",
    alt: "Cammy and Cheese",
    caption: `A mimir 😴💤`,
  },
  {
    id: "pet-cammy-cheese-2026-02-11-v01",
    type: "pet",
    date: "2026-02-11",
    image: "/images/blog/cammy-cheese-2026-02-11-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2026-02-11-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `i love giving pets just as much as i love getting them`,
  },
  {
    id: "pet-cammy-cheese-2026-01-21-p01",
    type: "pet",
    date: "2026-01-21",
    image: "/images/blog/cammy-cheese-2026-01-21-p01.webp",
    alt: "Cammy and Cheese",
    caption: `1️⃣3️⃣!!! 🎈🎊🎁`,
  },
  {
    id: "pet-cammy-cheese-2026-01-04-p01",
    type: "pet",
    date: "2026-01-04",
    image: "/images/blog/cammy-cheese-2026-01-04-p01.webp",
    alt: "Cammy and Cheese",
    caption: `It’s a bit big on me right now, but that’s ok, i’ll grow into it 🥹`,
  },
  {
    id: "pet-cammy-cheese-2026-01-03-p01",
    type: "pet",
    date: "2026-01-03",
    image: "/images/blog/cammy-cheese-2026-01-03-p01.webp",
    alt: "Cammy and Cheese",
    caption: `💕`,
  },
  {
    id: "pet-cammy-cheese-2025-12-30-p01",
    type: "pet",
    date: "2025-12-30",
    image: "/images/blog/cammy-cheese-2025-12-30-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Feliz holidays 🎄🎁❤️`,
  },
  {
    id: "pet-cammy-cheese-2025-12-23-p01",
    type: "pet",
    date: "2025-12-23",
    image: "/images/blog/cammy-cheese-2025-12-23-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🥶❄️🔃`,
  },
  {
    id: "pet-cammy-cheese-2025-12-13-p01",
    type: "pet",
    date: "2025-12-13",
    image: "/images/blog/cammy-cheese-2025-12-13-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🎁🎄🤭`,
  },
  {
    id: "pet-cammy-cheese-2025-12-13-p02",
    type: "pet",
    date: "2025-12-13",
    image: "/images/blog/cammy-cheese-2025-12-13-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-11-24-p01",
    type: "pet",
    date: "2025-11-24",
    image: "/images/blog/cammy-cheese-2025-11-24-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Happy birthday uncle Marco. 🎉🥳`,
  },
  {
    id: "pet-cammy-cheese-2025-11-21-p01",
    type: "pet",
    date: "2025-11-21",
    image: "/images/blog/cammy-cheese-2025-11-21-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😩🥱😪`,
  },
  {
    id: "pet-cammy-cheese-2025-11-15-p01",
    type: "pet",
    date: "2025-11-15",
    image: "/images/blog/cammy-cheese-2025-11-15-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Adonde vamos, amigis?`,
  },
  {
    id: "pet-cammy-cheese-2025-11-12-p01",
    type: "pet",
    date: "2025-11-12",
    image: "/images/blog/cammy-cheese-2025-11-12-p01.webp",
    alt: "Cammy and Cheese",
    caption: `With my bestie, Ezra. 💕🫶`,
  },
  {
    id: "pet-cammy-cheese-2025-10-29-p01",
    type: "pet",
    date: "2025-10-29",
    image: "/images/blog/cammy-cheese-2025-10-29-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Back together again`,
  },
  {
    id: "pet-cammy-cheese-2025-10-25-p01",
    type: "pet",
    date: "2025-10-25",
    image: "/images/blog/cammy-cheese-2025-10-25-p01.webp",
    alt: "Cammy and Cheese",
    caption: `At work with Papa 👀👨‍💻📈`,
  },
  {
    id: "pet-cammy-cheese-2025-10-21-p01",
    type: "pet",
    date: "2025-10-21",
    image: "/images/blog/cammy-cheese-2025-10-21-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🧀 CHEESE-FANSISCO`,
  },
  {
    id: "pet-cammy-cheese-2025-10-20-p01",
    type: "pet",
    date: "2025-10-20",
    image: "/images/blog/cammy-cheese-2025-10-20-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Fell sleep watching TV 🛏️`,
  },
  {
    id: "pet-cammy-cheese-2025-10-15-p01",
    type: "pet",
    date: "2025-10-15",
    image: "/images/blog/cammy-cheese-2025-10-15-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Today I graduate from puppy kindergarten 🎓`,
  },
  {
    id: "pet-cammy-cheese-2025-10-02-p01",
    type: "pet",
    date: "2025-10-02",
    image: "/images/blog/cammy-cheese-2025-10-02-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🙏`,
  },
  {
    id: "pet-cammy-cheese-2025-10-01-p01",
    type: "pet",
    date: "2025-10-01",
    image: "/images/blog/cammy-cheese-2025-10-01-p01.webp",
    alt: "Cammy and Cheese",
    caption: `☀️😴`,
  },
  {
    id: "pet-cammy-cheese-2025-09-25-p01",
    type: "pet",
    date: "2025-09-25",
    image: "/images/blog/cammy-cheese-2025-09-25-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-09-23-p01",
    type: "pet",
    date: "2025-09-23",
    image: "/images/blog/cammy-cheese-2025-09-23-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😴❤️`,
  },
  {
    id: "pet-cammy-cheese-2025-09-18-p01",
    type: "pet",
    date: "2025-09-18",
    image: "/images/blog/cammy-cheese-2025-09-18-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🍎`,
  },
  {
    id: "pet-cammy-cheese-2025-09-13-p01",
    type: "pet",
    date: "2025-09-13",
    image: "/images/blog/cammy-cheese-2025-09-13-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🐾`,
  },
  {
    id: "pet-cammy-cheese-2025-09-11-p01",
    type: "pet",
    date: "2025-09-11",
    image: "/images/blog/cammy-cheese-2025-09-11-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-09-11-p02",
    type: "pet",
    date: "2025-09-11",
    image: "/images/blog/cammy-cheese-2025-09-11-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-09-02-p01",
    type: "pet",
    date: "2025-09-02",
    image: "/images/blog/cammy-cheese-2025-09-02-p01.webp",
    alt: "Cammy and Cheese",
    caption: `☀️😴☀️`,
  },
  {
    id: "pet-cammy-cheese-2025-08-25-v01",
    type: "pet",
    date: "2025-08-25",
    image: "/images/blog/cammy-cheese-2025-08-25-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-08-25-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🌽My new toy!`,
  },
  {
    id: "pet-cammy-cheese-2025-08-17-v01",
    type: "pet",
    date: "2025-08-17",
    image: "/images/blog/cammy-cheese-2025-08-17-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-08-17-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🐾🐕💨`,
  },
  {
    id: "pet-cammy-cheese-2025-08-12-p01",
    type: "pet",
    date: "2025-08-12",
    image: "/images/blog/cammy-cheese-2025-08-12-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🛁🫧✂️💅`,
  },
  {
    id: "pet-cammy-cheese-2025-08-10-p01",
    type: "pet",
    date: "2025-08-10",
    image: "/images/blog/cammy-cheese-2025-08-10-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-08-10-p02",
    type: "pet",
    date: "2025-08-10",
    image: "/images/blog/cammy-cheese-2025-08-10-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-08-04-v01",
    type: "pet",
    date: "2025-08-04",
    image: "/images/blog/cammy-cheese-2025-08-04-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-08-04-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `MAYA!!! ☺️🍃`,
  },
  {
    id: "pet-cammy-cheese-2025-08-02-p01",
    type: "pet",
    date: "2025-08-02",
    image: "/images/blog/cammy-cheese-2025-08-02-p01.webp",
    alt: "Cammy and Cheese",
    caption: `We gave cousin Maya a pedicure!`,
  },
  {
    id: "pet-cammy-cheese-2025-07-27-p01",
    type: "pet",
    date: "2025-07-27",
    image: "/images/blog/cammy-cheese-2025-07-27-p01.webp",
    alt: "Cammy and Cheese",
    caption: `👀🏡`,
  },
  {
    id: "pet-cammy-cheese-2025-07-24-p01",
    type: "pet",
    date: "2025-07-24",
    image: "/images/blog/cammy-cheese-2025-07-24-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Howdy 🤠`,
  },
  {
    id: "pet-cammy-cheese-2025-07-08-v01",
    type: "pet",
    date: "2025-07-08",
    image: "/images/blog/cammy-cheese-2025-07-08-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-07-08-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `😋💅`,
  },
  {
    id: "pet-cammy-cheese-2025-07-07-p01",
    type: "pet",
    date: "2025-07-07",
    image: "/images/blog/cammy-cheese-2025-07-07-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🤨`,
  },
  {
    id: "pet-cammy-cheese-2025-06-29-p01",
    type: "pet",
    date: "2025-06-29",
    image: "/images/blog/cammy-cheese-2025-06-29-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🛌💤😴`,
  },
  {
    id: "pet-cammy-cheese-2025-06-03-v01",
    type: "pet",
    date: "2025-06-03",
    image: "/images/blog/cammy-cheese-2025-06-03-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-06-03-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `2️⃣🎂🎉🥳`,
  },
  {
    id: "pet-cammy-cheese-2025-05-29-p01",
    type: "pet",
    date: "2025-05-29",
    image: "/images/blog/cammy-cheese-2025-05-29-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-05-20-p01",
    type: "pet",
    date: "2025-05-20",
    image: "/images/blog/cammy-cheese-2025-05-20-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🛏🤫😴`,
  },
  {
    id: "pet-cammy-cheese-2025-05-19-p01",
    type: "pet",
    date: "2025-05-19",
    image: "/images/blog/cammy-cheese-2025-05-19-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😻🥰🫂`,
  },
  {
    id: "pet-cammy-cheese-2025-05-16-p01",
    type: "pet",
    date: "2025-05-16",
    image: "/images/blog/cammy-cheese-2025-05-16-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🍎❤️🥰`,
  },
  {
    id: "pet-cammy-cheese-2025-05-12-p01",
    type: "pet",
    date: "2025-05-12",
    image: "/images/blog/cammy-cheese-2025-05-12-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🤗🐾`,
  },
  {
    id: "pet-cammy-cheese-2025-05-11-p01",
    type: "pet",
    date: "2025-05-11",
    image: "/images/blog/cammy-cheese-2025-05-11-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🍇🍇🍇`,
  },
  {
    id: "pet-cammy-cheese-2025-05-02-p01",
    type: "pet",
    date: "2025-05-02",
    image: "/images/blog/cammy-cheese-2025-05-02-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🐕🐾✂️`,
  },
  {
    id: "pet-cammy-cheese-2025-04-25-p01",
    type: "pet",
    date: "2025-04-25",
    image: "/images/blog/cammy-cheese-2025-04-25-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🫘🫘`,
  },
  {
    id: "pet-cammy-cheese-2025-04-24-p01",
    type: "pet",
    date: "2025-04-24",
    image: "/images/blog/cammy-cheese-2025-04-24-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😍🤗✨️`,
  },
  {
    id: "pet-cammy-cheese-2025-04-23-p01",
    type: "pet",
    date: "2025-04-23",
    image: "/images/blog/cammy-cheese-2025-04-23-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🌸🌸🌸`,
  },
  {
    id: "pet-cammy-cheese-2025-04-14-p01",
    type: "pet",
    date: "2025-04-14",
    image: "/images/blog/cammy-cheese-2025-04-14-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🌭`,
  },
  {
    id: "pet-cammy-cheese-2025-04-09-p01",
    type: "pet",
    date: "2025-04-09",
    image: "/images/blog/cammy-cheese-2025-04-09-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🧳🛩`,
  },
  {
    id: "pet-cammy-cheese-2025-04-09-p02",
    type: "pet",
    date: "2025-04-09",
    image: "/images/blog/cammy-cheese-2025-04-09-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-04-06-p01",
    type: "pet",
    date: "2025-04-06",
    image: "/images/blog/cammy-cheese-2025-04-06-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🍑😴🍑`,
  },
  {
    id: "pet-cammy-cheese-2025-04-05-v01",
    type: "pet",
    date: "2025-04-05",
    image: "/images/blog/cammy-cheese-2025-04-05-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-04-05-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `💆‍♀️`,
  },
  {
    id: "pet-cammy-cheese-2025-03-28-p01",
    type: "pet",
    date: "2025-03-28",
    image: "/images/blog/cammy-cheese-2025-03-28-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🌱⛅️💤`,
  },
  {
    id: "pet-cammy-cheese-2025-03-24-p01",
    type: "pet",
    date: "2025-03-24",
    image: "/images/blog/cammy-cheese-2025-03-24-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🥰`,
  },
  {
    id: "pet-cammy-cheese-2025-03-24-v01",
    type: "pet",
    date: "2025-03-24",
    image: "/images/blog/cammy-cheese-2025-03-24-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-03-24-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🧻`,
  },
  {
    id: "pet-cammy-cheese-2025-03-17-v01",
    type: "pet",
    date: "2025-03-17",
    image: "/images/blog/cammy-cheese-2025-03-17-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-03-17-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `The cousins! 👯‍♀️`,
  },
  {
    id: "pet-cammy-cheese-2025-03-14-p01",
    type: "pet",
    date: "2025-03-14",
    image: "/images/blog/cammy-cheese-2025-03-14-p01.webp",
    alt: "Cammy and Cheese",
    caption: `3️⃣🐶😾🐱`,
  },
  {
    id: "pet-cammy-cheese-2025-03-12-p01",
    type: "pet",
    date: "2025-03-12",
    image: "/images/blog/cammy-cheese-2025-03-12-p01.webp",
    alt: "Cammy and Cheese",
    caption: `✨💕`,
  },
  {
    id: "pet-cammy-cheese-2025-03-11-v01",
    type: "pet",
    date: "2025-03-11",
    image: "/images/blog/cammy-cheese-2025-03-11-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-03-11-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🐕💨💨💨`,
  },
  {
    id: "pet-cammy-cheese-2025-03-05-p01",
    type: "pet",
    date: "2025-03-05",
    image: "/images/blog/cammy-cheese-2025-03-05-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🫂`,
  },
  {
    id: "pet-cammy-cheese-2025-02-28-p01",
    type: "pet",
    date: "2025-02-28",
    image: "/images/blog/cammy-cheese-2025-02-28-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🇮🇹`,
  },
  {
    id: "pet-cammy-cheese-2025-02-28-p02",
    type: "pet",
    date: "2025-02-28",
    image: "/images/blog/cammy-cheese-2025-02-28-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-02-27-v01",
    type: "pet",
    date: "2025-02-27",
    image: "/images/blog/cammy-cheese-2025-02-27-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-02-27-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🍦`,
  },
  {
    id: "pet-cammy-cheese-2025-02-26-v01",
    type: "pet",
    date: "2025-02-26",
    image: "/images/blog/cammy-cheese-2025-02-26-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-02-26-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🧦`,
  },
  {
    id: "pet-cammy-cheese-2025-02-18-p01",
    type: "pet",
    date: "2025-02-18",
    image: "/images/blog/cammy-cheese-2025-02-18-p01.webp",
    alt: "Cammy and Cheese",
    caption: `👧`,
  },
  {
    id: "pet-cammy-cheese-2025-02-13-p01",
    type: "pet",
    date: "2025-02-13",
    image: "/images/blog/cammy-cheese-2025-02-13-p01.webp",
    alt: "Cammy and Cheese",
    caption: `💖🍓🧀`,
  },
  {
    id: "pet-cammy-cheese-2025-02-10-p02",
    type: "pet",
    date: "2025-02-10",
    image: "/images/blog/cammy-cheese-2025-02-10-p02.webp",
    alt: "Cammy and Cheese",
    caption: `🤫😴`,
  },
  {
    id: "pet-cammy-cheese-2025-02-10-p01",
    type: "pet",
    date: "2025-02-10",
    image: "/images/blog/cammy-cheese-2025-02-10-p01.webp",
    alt: "Cammy and Cheese",
    caption: `Cheese is officially in post-op recovery for 2 weeks! 🧀❤️‍🩹🥹✂️`,
  },
  {
    id: "pet-cammy-cheese-2025-02-06-p01",
    type: "pet",
    date: "2025-02-06",
    image: "/images/blog/cammy-cheese-2025-02-06-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😴😻`,
  },
  {
    id: "pet-cammy-cheese-2025-02-02-p01",
    type: "pet",
    date: "2025-02-02",
    image: "/images/blog/cammy-cheese-2025-02-02-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🧧🧧🧧`,
  },
  {
    id: "pet-cammy-cheese-2025-01-29-p01",
    type: "pet",
    date: "2025-01-29",
    image: "/images/blog/cammy-cheese-2025-01-29-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-01-29-p02",
    type: "pet",
    date: "2025-01-29",
    image: "/images/blog/cammy-cheese-2025-01-29-p02.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-01-29-v01",
    type: "pet",
    date: "2025-01-29",
    image: "/images/blog/cammy-cheese-2025-01-29-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-29-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🧧🍊🎉`,
  },
  {
    id: "pet-cammy-cheese-2025-01-27-v01",
    type: "pet",
    date: "2025-01-27",
    image: "/images/blog/cammy-cheese-2025-01-27-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-27-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `😱😍🥹🥰`,
  },
  {
    id: "pet-cammy-cheese-2025-01-24-p01",
    type: "pet",
    date: "2025-01-24",
    image: "/images/blog/cammy-cheese-2025-01-24-p01.webp",
    alt: "Cammy and Cheese",
    caption: `❣️🥰`,
  },
  {
    id: "pet-cammy-cheese-2025-01-23-p01",
    type: "pet",
    date: "2025-01-23",
    image: "/images/blog/cammy-cheese-2025-01-23-p01.webp",
    alt: "Cammy and Cheese",
    caption: `😴🎮`,
  },
  {
    id: "pet-cammy-cheese-2025-01-22-v01",
    type: "pet",
    date: "2025-01-22",
    image: "/images/blog/cammy-cheese-2025-01-22-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-22-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🌮`,
  },
  {
    id: "pet-cammy-cheese-2025-01-21-v01",
    type: "pet",
    date: "2025-01-21",
    image: "/images/blog/cammy-cheese-2025-01-21-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-21-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `1️⃣2️⃣🩵🎉🥳`,
  },
  {
    id: "pet-cammy-cheese-2025-01-20-v02",
    type: "pet",
    date: "2025-01-20",
    image: "/images/blog/cammy-cheese-2025-01-20-v02-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-20-v02.mp4",
    alt: "Cammy and Cheese",
    caption: `🥹🎓`,
  },
  {
    id: "pet-cammy-cheese-2025-01-20-v01",
    type: "pet",
    date: "2025-01-20",
    image: "/images/blog/cammy-cheese-2025-01-20-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-20-v01.mp4",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-01-20-p01",
    type: "pet",
    date: "2025-01-20",
    image: "/images/blog/cammy-cheese-2025-01-20-p01.webp",
    alt: "Cammy and Cheese",
    caption: `❄️🌨🏂`,
  },
  {
    id: "pet-cammy-cheese-2025-01-16-p01",
    type: "pet",
    date: "2025-01-16",
    image: "/images/blog/cammy-cheese-2025-01-16-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🥹❤️😴`,
  },
  {
    id: "pet-cammy-cheese-2025-01-14-p01",
    type: "pet",
    date: "2025-01-14",
    image: "/images/blog/cammy-cheese-2025-01-14-p01.webp",
    alt: "Cammy and Cheese",
    caption: `👻`,
  },
  {
    id: "pet-cammy-cheese-2025-01-12-p01",
    type: "pet",
    date: "2025-01-12",
    image: "/images/blog/cammy-cheese-2025-01-12-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🐶🖥🖱🐱`,
  },
  {
    id: "pet-cammy-cheese-2025-01-11-p01",
    type: "pet",
    date: "2025-01-11",
    image: "/images/blog/cammy-cheese-2025-01-11-p01.webp",
    alt: "Cammy and Cheese",
    caption: `❄️🌨☃️`,
  },
  {
    id: "pet-cammy-cheese-2025-01-11-v01",
    type: "pet",
    date: "2025-01-11",
    image: "/images/blog/cammy-cheese-2025-01-11-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-11-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `🖊`,
  },
  {
    id: "pet-cammy-cheese-2025-01-10-p01",
    type: "pet",
    date: "2025-01-10",
    image: "/images/blog/cammy-cheese-2025-01-10-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🍔`,
  },
  {
    id: "pet-cammy-cheese-2025-01-09-p02",
    type: "pet",
    date: "2025-01-09",
    image: "/images/blog/cammy-cheese-2025-01-09-p02.webp",
    alt: "Cammy and Cheese",
    caption: `🎩`,
  },
  {
    id: "pet-cammy-cheese-2025-01-09-p01",
    type: "pet",
    date: "2025-01-09",
    image: "/images/blog/cammy-cheese-2025-01-09-p01.webp",
    alt: "Cammy and Cheese",
  },
  {
    id: "pet-cammy-cheese-2025-01-08-v01",
    type: "pet",
    date: "2025-01-08",
    image: "/images/blog/cammy-cheese-2025-01-08-v01-poster.webp",
    video: "/videos/blog/cammy-cheese-2025-01-08-v01.mp4",
    alt: "Cammy and Cheese",
    caption: `👋😳😴`,
  },
  {
    id: "pet-cammy-cheese-2025-01-07-p01",
    type: "pet",
    date: "2025-01-07",
    image: "/images/blog/cammy-cheese-2025-01-07-p01.webp",
    alt: "Cammy and Cheese",
    caption: `🐰`,
  },
  {
    id: "pet-cammy-cheese-2025-01-06-p03",
    type: "pet",
    date: "2025-01-06",
    image: "/images/blog/cammy-cheese-2025-01-06-p03.webp",
    alt: "Cammy and Cheese",
    caption: `🥱😴`,
  },
  {
    id: "pet-cammy-cheese-2025-01-06-p02",
    type: "pet",
    date: "2025-01-06",
    image: "/images/blog/cammy-cheese-2025-01-06-p02.webp",
    alt: "Cammy and Cheese",
    caption: `Good night everyone!`,
  },
  {
    id: "pet-cammy-cheese-2025-01-06-p01",
    type: "pet",
    date: "2025-01-06",
    image: "/images/blog/cammy-cheese-2025-01-06-p01.webp",
    alt: "Cammy and Cheese",
  },
];
